import React from 'react';
import { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import './classes/ConferenceRegistration.css';

import { configData } from "../config";

// The sign-up popup behind the "Be The First To Know" button on conference pages.
// Same fields, checks and wording as the Drupal popup block; it posts to the
// existing smwwdrupal/bethefirsttoknow endpoint.

// Values are sent as tags exactly as the Drupal form sent them
const CONFERENCES = [
    { value: "Baseball Career Conference", label: "Baseball Career Conference" },
    { value: "Basketball Career Conference", label: "Basketball Career Conference" },
    { value: "NFL Combine Football Career Conference", label: "NFL Combine Football Career Conference" },
    { value: "NHL Draft Hockey Career Conference", label: "NHL Draft Hockey Career Conference" },
    { value: " Race Track Business Conference ", label: "Race Track Business Conference" },
    { value: "Soccer Career Conference at the NWSL Draft", label: "Soccer Career Conference at the NWSL Draft" },
    { value: "Toronto PrimeTime Sports Conference", label: "Toronto PrimeTime Sports Conference" },
];

// Addresses the Drupal form refuses
const REFUSED_EMAILS = ['regan.jim65@gmail.com', 'jamesfregan68@gmail.com', 'lisaostrom90@gmail.com'];

const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const BeTheFirstToKnowPopup = ({ open, onClose }) => {
    const app_url = configData.APP_URL;
    const sitekey = configData.GOOGLE_RECAPTCHA_SITE_KEY;
    const recaptchaRef = useRef(null);

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [tags, setTags] = useState([]);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Start from an empty form each time the popup opens
    useEffect(() => {
        if (open) {
            setEmail('');
            setFirstName('');
            setLastName('');
            setTags([]);
            setError('');
            setIsSubmitting(false);
            setIsSubmitted(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [open, onClose]);

    if (!open) return null;

    const toggleTag = (value) => {
        setTags((current) => current.includes(value) ? current.filter((tag) => tag !== value) : [...current, value]);
    };

    const validate = (recaptchaValue) => {
        const lowerEmail = email.toLowerCase();

        if (email === '') return 'Please enter your email';
        if (lowerEmail === 'info@smww.com') return 'Please enter your email address (cannot be info@smww.com)';
        if (REFUSED_EMAILS.includes(lowerEmail)) return 'Please enter your email address.';
        if (!EMAIL_PATTERN.test(email)) return 'Invalid email address';
        if (firstName.trim() === '') return 'Please enter your first name';
        if (lastName.trim() === '') return 'Please enter your last name';
        if (tags.length === 0) return 'Please select at least one conference.';
        if (!recaptchaValue) return "Please verify you're not a robot.";

        return '';
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const recaptchaValue = recaptchaRef.current ? recaptchaRef.current.getValue() : '';
        const validationError = validate(recaptchaValue);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        setIsSubmitting(true);
        try {
            const response = await fetch(app_url + 'smwwdrupal/bethefirsttoknow', {
                method: 'POST',
                body: JSON.stringify({
                    email: email,
                    custom_fields: {
                        source: 'conference',
                        first_name: firstName,
                        last_name: lastName,
                    },
                    tags: tags,
                    pageURL: window.location.href,
                    'g-recaptcha-response': recaptchaValue,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (data?.data?.result === "user_created") {
                setIsSubmitted(true);
            } else {
                setError('Sorry, something went wrong. Please try again.');
            }
        } catch (err) {
            setError('Sorry, something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="btftk-overlay" onClick={onClose}>
            <div className="btftk-dialog" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
                <div className="btftk-titlebar">
                    <span>{isSubmitted ? 'Success!' : 'Be The First To Know When The Next Conference Date Is Announced'}</span>
                    <button type="button" className="btftk-close" aria-label="Close" onClick={onClose}>&times;</button>
                </div>

                {isSubmitted ? (
                    <p className="btftk-success">Thank you! We will let you know as soon as conference dates are announced!</p>
                ) : (
                    <form className="btftk-form" onSubmit={handleSubmit} noValidate>
                        <div className="form-group">
                            <label htmlFor="btftk-email"><span className="required">*</span> Email address</label>
                            <input className="form-control" id="btftk-email" name="email" type="text" value={email} onChange={(event) => setEmail(event.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="btftk-firstname"><span className="required">*</span> First Name</label>
                            <input className="form-control" id="btftk-firstname" name="first_name" type="text" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="btftk-lastname"><span className="required">*</span> Last Name</label>
                            <input className="form-control" id="btftk-lastname" name="last_name" type="text" value={lastName} onChange={(event) => setLastName(event.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Conference</label>
                            {CONFERENCES.map((conference) => (
                                <label className="i-checks" key={conference.value}>
                                    <input name="tags[]" type="checkbox" value={conference.value} checked={tags.includes(conference.value)} onChange={() => toggleTag(conference.value)} />
                                    {conference.label}
                                </label>
                            ))}
                        </div>
                        <div className="form-group btftk-recaptcha">
                            <ReCAPTCHA ref={recaptchaRef} sitekey={sitekey} />
                        </div>

                        {error && <div className="btftk-error" role="alert">{error}</div>}

                        <div className="btftk-actions">
                            <button type="submit" className="btftk-submit" disabled={isSubmitting}>
                                I Want To Be The First To Know When Conference Dates Are Announced!
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
export default BeTheFirstToKnowPopup;
